import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Lightbox } from "./Lightbox";
import { INTERLUDIO } from "./Carrellata";

import API from "../apiBase";

/* Posizioni casuali ma stabili: ogni ricordo sta sempre
   nello stesso punto del suo cielo */
const seeded = (i, salt) => {
  const x = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

const posizioni = (n, blocco) =>
  Array.from({ length: n }, (_, i) => ({
    left: 12 + seeded(i, blocco * 3 + 1) * 66,
    top: ((i + 0.7) / (n + 0.4)) * 100 + (seeded(i, blocco * 5 + 2) - 0.5) * 5,
  }));

/* I ricordi nascosti tra le stelle: ogni blocco della storia è una
   costellazione. Tocca una stella dorata e il ricordo appare piccolo
   accanto a lei; tocca il ricordo e si apre a schermo intero. */
export const CieloRicordi = () => {
  const [blocks, setBlocks] = useState(null);
  const [found, setFound] = useState({});
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    Promise.all(
      [1, 2, 3, 4].map((n) =>
        fetch(`${API}/api/media/${n}`)
          .then((r) => (r.ok ? r.json() : { items: [], nome: "" }))
          .catch(() => ({ items: [], nome: "" }))
      )
    ).then((results) =>
      setBlocks(results.map((r) => ({ nome: r.nome || "", items: r.items })))
    );
  }, []);

  const media = blocks ? blocks.flatMap((b) => b.items) : [];
  const total = media.length;
  const scoperti = Object.keys(found).length;

  /* mappa del tesoro: la prossima stella della storia brilla più forte */
  const nextIndex = media.findIndex((m) => !found[m.src]);

  /* quando il cielo è completo, una stella ti avvisa (una volta per apertura) */
  const segnalato = useRef(false);
  useEffect(() => {
    if (total > 0 && scoperti === total && !segnalato.current) {
      segnalato.current = true;
      fetch(`${API}/api/traguardo`, { method: "POST" }).catch(() => {});
    }
  }, [scoperti, total]);

  const apriRicordo = (src) => {
    const i = media.findIndex((m) => m.src === src);
    if (i >= 0) setLightbox(i);
  };

  /* l'ultima stella porta al finale della storia */
  const vaiAlFinale = () => {
    const el = document.querySelector(".present");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { duration: 2.2 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  let num = 0;

  return (
    <main className="cielo-ricordi" data-testid="cielo-ricordi">
      <motion.div
        className="carrellata-head"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <p className="carrellata-eyebrow">la nostra storia nel cielo</p>
        <h2 className="carrellata-title">Cerca i ricordi tra le stelle.</h2>
        <p className="carrellata-hint">
          le stelle dorate nascondono un ricordo — segui quella che brilla più
          forte
        </p>
        {total > 0 && (
          <p className="cielo-progress" data-testid="cielo-progress">
            {scoperti} di {total} ricordi scoperti
          </p>
        )}
      </motion.div>

      {(blocks || []).map((b, bi) => {
        if (!b.items.length) return null;
        const pos = posizioni(b.items.length, bi + 1);
        const startNum = num;
        num += b.items.length;
        /* la scia collega le stelle scoperte, in ordine cronologico */
        const foundIdx = b.items
          .map((it, i) => (found[it.src] ? i : -1))
          .filter((i) => i >= 0);
        return (
          <div key={bi} className="costellazione">
            {bi === 2 && (
              <motion.div
                className="interludio"
                data-testid="interludio"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="interludio-star">✦</span>
                {INTERLUDIO.map((p, i) => (
                  <p
                    key={i}
                    className={i === INTERLUDIO.length - 1 ? "interludio-last" : ""}
                  >
                    {p}
                  </p>
                ))}
                <span className="interludio-star">✦</span>
              </motion.div>
            )}
            {b.nome && (
              <motion.div
                className="carrellata-divider"
                data-testid={`costellazione-nome-${bi + 1}`}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1 }}
              >
                <span className="carrellata-divider-line" />
                <span className="carrellata-divider-label">{b.nome}</span>
                <span className="carrellata-divider-line" />
              </motion.div>
            )}
            <section
              className="cielo-panel"
              style={{ minHeight: `max(92vh, ${b.items.length * 128}px)` }}
              data-testid={`costellazione-${bi + 1}`}
            >
              <svg
                className="cielo-lines"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <motion.polyline
                  points={pos.map((p) => `${p.left},${p.top}`).join(" ")}
                  fill="none"
                  stroke="rgba(230, 185, 111, 0.2)"
                  strokeWidth="0.18"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 2.4, ease: "easeInOut" }}
                />
                {pos.map((p, i) => (
                  <circle
                    key={i}
                    cx={p.left}
                    cy={p.top}
                    r="0.3"
                    fill="rgba(230, 185, 111, 0.45)"
                  />
                ))}
                {foundIdx.slice(1).map((idx, k) => {
                  const a = pos[foundIdx[k]];
                  const c = pos[idx];
                  return (
                    <g key={`scia-${bi}-${idx}`}>
                      <motion.line
                        className="scia-glow"
                        x1={a.left}
                        y1={a.top}
                        x2={c.left}
                        y2={c.top}
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.9, ease: "easeOut" }}
                      />
                      <motion.line
                        className="scia-line"
                        data-testid={`scia-${bi}-${idx}`}
                        x1={a.left}
                        y1={a.top}
                        x2={c.left}
                        y2={c.top}
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.9, ease: "easeOut" }}
                      />
                    </g>
                  );
                })}
              </svg>
              {b.items.map((item, i) => {
                const g = startNum + i;
                const p = pos[i];
                const isFound = !!found[item.src];
                const isNext = g === nextIndex;
                const flip = p.left > 52;
                const isVideo = item.type === "video";
                return (
                  <div key={item.src}>
                    <motion.button
                      className={`stella-ricordo${isFound ? " stella-ricordo--found" : ""}${isNext ? " stella-ricordo--next" : ""}`}
                      style={{ left: `${p.left}%`, top: `${p.top}%` }}
                      onClick={() =>
                        setFound((f) => {
                          const n = { ...f };
                          if (n[item.src]) delete n[item.src];
                          else n[item.src] = true;
                          return n;
                        })
                      }
                      aria-label={
                        isFound
                          ? `Nascondi il ricordo ${g + 1}`
                          : `Scopri il ricordo ${g + 1}`
                      }
                      data-testid={`stella-${g}`}
                      initial={{ opacity: 0, scale: 0, x: "-50%", y: "-50%" }}
                      whileInView={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ delay: i * 0.07, duration: 0.6 }}
                      whileTap={{ scale: 1.4 }}
                    >
                      {isNext && (
                        <>
                          <span className="stella-ring" aria-hidden="true" />
                          <span
                            className="stella-ring stella-ring--late"
                            aria-hidden="true"
                          />
                        </>
                      )}
                      <svg
                        className="stella-icon"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path d="M12 1.8 L15 8.3 L22 9.1 L16.8 13.9 L18.4 20.8 L12 17.1 L5.6 20.8 L7.2 13.9 L2 9.1 L9 8.3 Z" />
                      </svg>
                    </motion.button>
                    {isFound && (
                      <motion.div
                        className="stella-reveal"
                        style={{
                          top: `${p.top}%`,
                          ...(flip
                            ? { right: `${100 - p.left + 4}%` }
                            : { left: `${p.left + 4}%` }),
                        }}
                        initial={{ opacity: 0, scale: 0.4, y: "-50%" }}
                        animate={{ opacity: 1, scale: 1, y: "-50%" }}
                        transition={{
                          type: "spring",
                          stiffness: 260,
                          damping: 20,
                        }}
                        data-testid={`reveal-${g}`}
                      >
                        <div
                          className="reveal-frame"
                          onClick={() => apriRicordo(item.src)}
                          role="button"
                          aria-label={
                            isVideo
                              ? "Apri il video a schermo intero"
                              : "Apri la foto a schermo intero"
                          }
                        >
                          {isVideo ? (
                            <video
                              src={encodeURI(item.src)}
                              poster={
                                item.poster ? encodeURI(item.poster) : undefined
                              }
                              autoPlay
                              muted
                              loop
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <img
                              src={encodeURI(item.src)}
                              alt={item.caption || `Ricordo ${g + 1}`}
                              loading="lazy"
                            />
                          )}
                        </div>
                        {(item.caption || item.date) && (
                          <div className="reveal-info">
                            {item.caption && (
                              <p
                                className="reveal-caption"
                                data-testid={`reveal-caption-${g}`}
                              >
                                {item.caption}
                              </p>
                            )}
                            {item.date && (
                              <p
                                className="reveal-date"
                                data-testid={`reveal-date-${g}`}
                              >
                                {item.date}
                              </p>
                            )}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </section>
          </div>
        );
      })}

      {/* la stella finale appare solo quando il cielo è completo */}
      {total > 0 && scoperti === total && (
        <motion.section
          className="stella-finale"
          data-testid="stella-finale"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
        >
          <p className="stella-finale-msg">hai acceso tutte le stelle</p>
          <motion.button
            className="stella-finale-btn"
            onClick={vaiAlFinale}
            aria-label="Tocca l'ultima stella per arrivare al finale"
            data-testid="stella-finale-btn"
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 120, damping: 12, delay: 0.5 }}
            whileTap={{ scale: 0.92 }}
          >
            <span className="finale-ring" aria-hidden="true" />
            <span className="finale-ring finale-ring--late" aria-hidden="true" />
            <motion.svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            >
              <path d="M12 1.8 L15 8.3 L22 9.1 L16.8 13.9 L18.4 20.8 L12 17.1 L5.6 20.8 L7.2 13.9 L2 9.1 L9 8.3 Z" />
            </motion.svg>
          </motion.button>
          <p className="stella-finale-sub">quest'ultima è la nostra ✦ toccala</p>
        </motion.section>
      )}

      <Lightbox
        media={media}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onNav={setLightbox}
      />
    </main>
  );
};
